import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Inbox, FileText, LogOut, Trash2, Eye, EyeOff, Loader2, Save, ArrowLeft, Reply, Send, Images, BarChart3, MessageSquareQuote, Palette, Newspaper, Download } from "lucide-react";
import { api } from "../api";
import { MediaField, MEDIA_FIELDS } from "../components/admin/MediaField";
import { GalleryManager } from "../components/admin/GalleryManager";
import { StatsPanel } from "../components/admin/StatsPanel";
import { TestimonialsManager } from "../components/admin/TestimonialsManager";
import { DesignPanel } from "../components/admin/DesignPanel";
import { NewsManager } from "../components/admin/NewsManager";
import { useApp } from "../store";
import { DEFAULT_CONTENT } from "../defaults";

const GROUPS = [
  { label: "Hero", keys: ["hero_badge", "hero_title", "hero_subtitle", "marquee_items"] },
  { label: "Le Cabinet", keys: ["about_title", "about_text", "mission_title", "mission_text", "chapter_1_title", "chapter_1_text", "chapter_2_title", "chapter_2_text", "chapter_3_title", "chapter_3_text"] },
  { label: "Expertises", keys: Array.from({ length: 7 }, (_, i) => [`service_${i + 1}_title`, `service_${i + 1}_desc`]).flat() },
  { label: "Méthodologie", keys: Array.from({ length: 5 }, (_, i) => [`step_${i + 1}_title`, `step_${i + 1}_desc`]).flat() },
  { label: "Clients", keys: Array.from({ length: 8 }, (_, i) => [`client_${i + 1}_title`, `client_${i + 1}_desc`]).flat() },
  { label: "Valeurs", keys: Array.from({ length: 5 }, (_, i) => [`value_${i + 1}_title`, `value_${i + 1}_desc`]).flat() },
  { label: "Contact", keys: ["cta_title", "cta_text", "contact_email", "contact_phone", "contact_address"] },
  { label: "Vidéo", keys: ["video_title", "video_text"] },
  { label: "Mentions légales", keys: ["legal_title", "legal_text"] },
  { label: "Témoignages", keys: ["testimonials_title", "testimonials_sub"] },
  { label: "Annonce (bandeau en haut du site — laissez vide pour masquer)", keys: ["announcement_text"] },
];

const MULTILINE = (key) =>
  key === "hero_title" || key.endsWith("_desc") || key.endsWith("_text") || key === "hero_subtitle" || key === "cta_text" || key === "marquee_items" || key === "about_text";

export default function AdminDashboard() {
  const { t, setContent } = useApp();
  const navigate = useNavigate();
  const [tab, setTab] = useState("messages");
  const [messages, setMessages] = useState(null);
  const [fields, setFields] = useState({});
  const [saving, setSaving] = useState({});

  useEffect(() => {
    api.get("/auth/me").catch(() => navigate("/admin"));
  }, [navigate]);

  const loadMessages = () => {
    api
      .get("/admin/messages")
      .then((r) => setMessages(r.data.messages))
      .catch(() => navigate("/admin"));
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/messages/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "messages-polynesie-gestion-projet.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t("admin_save_error"));
    }
  };

  useEffect(() => {
    loadMessages();
    api.get("/content").then((r) => setFields(r.data.items || {})).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    localStorage.removeItem("pgp_token");
    navigate("/admin");
  };

  const toggleRead = async (msg) => {
    await api.patch(`/admin/messages/${msg.id}`, { read: !msg.read });
    loadMessages();
  };

  const remove = async (msg) => {
    await api.delete(`/admin/messages/${msg.id}`);
    loadMessages();
  };

  const [replyFor, setReplyFor] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replySending, setReplySending] = useState(false);
  const [uploading, setUploading] = useState({});

  const sendReply = async (msg) => {
    setReplySending(true);
    try {
      await api.post(`/admin/messages/${msg.id}/reply`, { text: replyText });
      toast.success(t("admin_reply_sent"));
      setReplyFor(null);
      setReplyText("");
      loadMessages();
    } catch {
      toast.error(t("admin_save_error"));
    } finally {
      setReplySending(false);
    }
  };

  const uploadMedia = async (key, file) => {
    setUploading((s) => ({ ...s, [key]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      const abs = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      await api.put(`/content/${key}`, { fr: abs, en: abs });
      setFields((f) => ({ ...f, [key]: { fr: abs, en: abs } }));
      setContent((prev) => ({ ...prev, [key]: { fr: abs, en: abs } }));
      toast.success(t("admin_saved"));
    } catch {
      toast.error(t("admin_save_error"));
    } finally {
      setUploading((s) => ({ ...s, [key]: false }));
    }
  };

  const setMediaText = (key, val) => {
    setFields((f) => ({ ...f, [key]: { fr: val, en: val } }));
  };

  const saveField = async (key) => {
    setSaving((s) => ({ ...s, [key]: true }));
    try {
      await api.put(`/content/${key}`, fields[key]);
      setContent((prev) => ({ ...prev, [key]: fields[key] }));
      toast.success(t("admin_saved"));
    } catch {
      toast.error(t("admin_save_error"));
    } finally {
      setSaving((s) => ({ ...s, [key]: false }));
    }
  };

  const setField = (key, lang, val) =>
    setFields((f) => ({ ...f, [key]: { ...f[key], [lang]: val } }));

  const unreadCount = (messages || []).filter((m) => !m.read).length;

  return (
    <div className="min-h-screen bg-[#0B132B] text-white" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-[#0B132B]/90 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="w-8 h-8 grid place-items-center bg-[var(--accent)] text-[#0B132B] font-display font-bold">G</span>
            <span className="font-mono2 text-xs uppercase tracking-[0.3em] text-white/60">Admin — PGP</span>
          </div>
          <div className="flex items-center gap-2">
            <Link
              data-testid="admin-back-to-site-link"
              to="/"
              className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white px-3 py-2 transition-colors duration-300"
            >
              <ArrowLeft size={15} /> {t("login_back")}
            </Link>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="inline-flex items-center gap-2 text-sm border border-white/15 hover:border-[var(--accent)] hover:text-[var(--accent)] px-4 py-2 rounded-full transition-colors duration-300"
            >
              <LogOut size={15} /> {t("admin_logout")}
            </button>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 flex gap-1">
          <button
            data-testid="admin-tab-messages"
            onClick={() => setTab("messages")}
            className={`inline-flex items-center gap-2 px-5 py-3 text-sm border-b-2 transition-colors duration-300 ${
              tab === "messages" ? "border-[var(--accent)] text-white" : "border-transparent text-white/50 hover:text-white"
            }`}
          >
            <Inbox size={16} />
            {t("admin_messages")}
            {unreadCount > 0 && (
              <span data-testid="admin-unread-badge" className="ml-1 bg-[var(--accent)] text-[#0B132B] font-mono2 text-[10px] px-2 py-0.5 rounded-full">
                {unreadCount}
              </span>
            )}
          </button>
          <button
            data-testid="admin-tab-content"
            onClick={() => setTab("content")}
            className={`inline-flex items-center gap-2 px-5 py-3 text-sm border-b-2 transition-colors duration-300 ${
              tab === "content" ? "border-[var(--accent)] text-white" : "border-transparent text-white/50 hover:text-white"
            }`}
          >
            <FileText size={16} />
            {t("admin_content")}
          </button>
          <button
            data-testid="admin-tab-gallery"
            onClick={() => setTab("gallery")}
            className={`inline-flex items-center gap-2 px-5 py-3 text-sm border-b-2 transition-colors duration-300 ${
              tab === "gallery" ? "border-[var(--accent)] text-white" : "border-transparent text-white/50 hover:text-white"
            }`}
          >
            <Images size={16} />
            {t("admin_gallery")}
          </button>
          <button
            data-testid="admin-tab-stats"
            onClick={() => setTab("stats")}
            className={`inline-flex items-center gap-2 px-5 py-3 text-sm border-b-2 transition-colors duration-300 ${
              tab === "stats" ? "border-[var(--accent)] text-white" : "border-transparent text-white/50 hover:text-white"
            }`}
          >
            <BarChart3 size={16} />
            {t("admin_stats")}
          </button>
          <button
            data-testid="admin-tab-testimonials"
            onClick={() => setTab("testimonials")}
            className={`inline-flex items-center gap-2 px-5 py-3 text-sm border-b-2 transition-colors duration-300 ${
              tab === "testimonials" ? "border-[var(--accent)] text-white" : "border-transparent text-white/50 hover:text-white"
            }`}
          >
            <MessageSquareQuote size={16} />
            {t("admin_testimonials")}
          </button>
          <button
            data-testid="admin-tab-design"
            onClick={() => setTab("design")}
            className={`inline-flex items-center gap-2 px-5 py-3 text-sm border-b-2 transition-colors duration-300 ${
              tab === "design" ? "border-[var(--accent)] text-white" : "border-transparent text-white/50 hover:text-white"
            }`}
          >
            <Palette size={16} />
            {t("admin_design")}
          </button>
          <button
            data-testid="admin-tab-news"
            onClick={() => setTab("news")}
            className={`inline-flex items-center gap-2 px-5 py-3 text-sm border-b-2 transition-colors duration-300 ${
              tab === "news" ? "border-[var(--accent)] text-white" : "border-transparent text-white/50 hover:text-white"
            }`}
          >
            <Newspaper size={16} />
            {t("admin_news")}
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        {tab === "messages" && (
          <div data-testid="admin-messages-panel" className="flex flex-col gap-4">
            <div className="flex justify-end">
              <button
                data-testid="admin-export-button"
                onClick={exportCsv}
                className="inline-flex items-center gap-2 text-xs border border-white/15 hover:border-[var(--accent)] hover:text-[var(--accent-soft)] px-4 py-2.5 rounded-full transition-colors duration-300"
              >
                <Download size={14} />
                {t("admin_export")}
              </button>
            </div>
            {messages === null ? (
              <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[var(--accent)]" size={28} /></div>
            ) : messages.length === 0 ? (
              <p data-testid="admin-messages-empty" className="text-white/45 py-20 text-center font-display italic text-xl">
                {t("admin_empty")}
              </p>
            ) : (
              messages.map((m) => (
                <article
                  key={m.id}
                  data-testid={`admin-message-${m.id}`}
                  className={`border p-6 transition-colors duration-300 ${
                    m.read ? "border-white/10 bg-white/[0.02]" : "border-[var(--accent)]/40 bg-[#172A45]/60"
                  }`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold">
                        {m.name}
                        {!m.read && (
                          <span className="ml-2 font-mono2 text-[10px] uppercase tracking-widest text-[var(--accent)]">
                            ● {t("unread")}
                          </span>
                        )}
                      </p>
                      <a href={`mailto:${m.email}`} className="text-sm text-[var(--accent)] hover:underline">{m.email}</a>
                      {m.subject && <p className="mt-1 text-sm text-white/60">{m.subject}</p>}
                    </div>
                    <span className="font-mono2 text-[11px] text-white/35">
                      {new Date(m.created_at).toLocaleString()}
                    </span>
                  </div>
                  <p className="mt-4 text-sm text-white/75 whitespace-pre-wrap leading-relaxed">{m.message}</p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    <button
                      data-testid={`admin-message-read-toggle-${m.id}`}
                      onClick={() => toggleRead(m)}
                      className="inline-flex items-center gap-2 text-xs border border-white/15 hover:border-white/40 px-3 py-2 rounded-full transition-colors duration-300"
                    >
                      {m.read ? <EyeOff size={13} /> : <Eye size={13} />}
                      {m.read ? t("admin_mark_unread") : t("admin_mark_read")}
                    </button>
                    <button
                      data-testid={`admin-message-reply-${m.id}`}
                      onClick={() => {
                        setReplyFor(replyFor === m.id ? null : m.id);
                        setReplyText("");
                      }}
                      className="inline-flex items-center gap-2 text-xs border border-white/15 hover:border-[var(--accent)] hover:text-[var(--accent)] px-3 py-2 rounded-full transition-colors duration-300"
                    >
                      <Reply size={13} />
                      {t("admin_reply")}
                    </button>
                    <button
                      data-testid={`admin-message-delete-${m.id}`}
                      onClick={() => remove(m)}
                      className="inline-flex items-center gap-2 text-xs border border-white/15 hover:border-[var(--accent)] hover:text-[var(--accent)] px-3 py-2 rounded-full transition-colors duration-300"
                    >
                      <Trash2 size={13} />
                      {t("admin_delete")}
                    </button>
                  </div>
                  {m.replied_at && (
                    <p data-testid={`admin-message-replied-${m.id}`} className="mt-3 font-mono2 text-[11px] text-white/35">
                      ✓ {t("admin_replied")} {new Date(m.replied_at).toLocaleString()}
                    </p>
                  )}
                  {replyFor === m.id && (
                    <div data-testid={`admin-reply-box-${m.id}`} className="mt-4 border border-[var(--accent)]/30 bg-white/[0.03] p-4">
                      <textarea
                        data-testid={`admin-reply-text-${m.id}`}
                        rows={4}
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        placeholder={t("admin_reply_placeholder")}
                        className="w-full bg-white/[0.05] border border-white/15 focus:border-[var(--accent)] focus:outline-none px-3 py-2.5 text-sm transition-colors duration-300 resize-y"
                      />
                      <div className="mt-3 flex gap-2">
                        <button
                          data-testid={`admin-reply-send-${m.id}`}
                          onClick={() => sendReply(m)}
                          disabled={replySending || replyText.trim().length < 2}
                          className="inline-flex items-center gap-2 text-xs bg-[var(--accent)] hover:bg-[var(--accent-soft)] disabled:opacity-60 text-white font-semibold px-4 py-2.5 rounded-full transition-colors duration-300"
                        >
                          {replySending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                          {replySending ? t("admin_reply_sending") : t("admin_reply_send")}
                        </button>
                        <button
                          data-testid={`admin-reply-cancel-${m.id}`}
                          onClick={() => setReplyFor(null)}
                          className="inline-flex items-center gap-2 text-xs border border-white/15 hover:border-white/40 px-4 py-2.5 rounded-full transition-colors duration-300"
                        >
                          {t("admin_cancel")}
                        </button>
                      </div>
                    </div>
                  )}
                </article>
              ))
            )}
          </div>
        )}

        {tab === "content" && (
          <div data-testid="admin-content-panel" className="flex flex-col gap-12">
            <div data-testid="admin-content-nav" className="flex flex-wrap gap-2 sticky top-[120px] z-30 bg-[#0B132B]/95 backdrop-blur py-2">
              {[...GROUPS.map((g) => g.label), t("admin_media")].map((label, i) => (
                <button
                  key={label}
                  data-testid={`admin-content-nav-${i}`}
                  onClick={() => document.getElementById(`content-section-${i}`)?.scrollIntoView({ behavior: "smooth", block: "start" })}
                  className="text-xs border border-white/15 hover:border-[var(--accent)] hover:text-[var(--accent)] text-white/60 px-4 py-2 rounded-full transition-colors duration-300"
                >
                  {label}
                </button>
              ))}
            </div>
            {GROUPS.map((group, i) => (
              <section key={group.label} id={`content-section-${i}`} className="scroll-mt-44">
                <h2 className="font-display font-semibold text-2xl mb-6 flex items-center gap-3">
                  <span className="w-6 h-px bg-[var(--accent)]" />
                  {group.label}
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {group.keys.map((key) => {
                    const val = fields[key] || DEFAULT_CONTENT[key] || { fr: "", en: "" };
                    const multi = MULTILINE(key);
                    return (
                      <div key={key} data-testid={`admin-field-${key}`} className="border border-white/10 bg-white/[0.02] p-5">
                        <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-white/40 mb-3">{key}</p>
                        <label className="block text-xs text-white/50 mb-1">{t("admin_fr")}</label>
                        {multi ? (
                          <textarea
                            data-testid={`admin-field-${key}-fr`}
                            rows={3}
                            value={val.fr}
                            onChange={(e) => setField(key, "fr", e.target.value)}
                            className="w-full bg-white/[0.05] border border-white/15 focus:border-[var(--accent)] focus:outline-none px-3 py-2.5 text-sm transition-colors duration-300 resize-y"
                          />
                        ) : (
                          <input
                            data-testid={`admin-field-${key}-fr`}
                            value={val.fr}
                            onChange={(e) => setField(key, "fr", e.target.value)}
                            className="w-full bg-white/[0.05] border border-white/15 focus:border-[var(--accent)] focus:outline-none px-3 py-2.5 text-sm transition-colors duration-300"
                          />
                        )}
                        <label className="block text-xs text-white/50 mt-3 mb-1">{t("admin_en")}</label>
                        {multi ? (
                          <textarea
                            data-testid={`admin-field-${key}-en`}
                            rows={3}
                            value={val.en}
                            onChange={(e) => setField(key, "en", e.target.value)}
                            className="w-full bg-white/[0.05] border border-white/15 focus:border-[var(--accent)] focus:outline-none px-3 py-2.5 text-sm transition-colors duration-300 resize-y"
                          />
                        ) : (
                          <input
                            data-testid={`admin-field-${key}-en`}
                            value={val.en}
                            onChange={(e) => setField(key, "en", e.target.value)}
                            className="w-full bg-white/[0.05] border border-white/15 focus:border-[var(--accent)] focus:outline-none px-3 py-2.5 text-sm transition-colors duration-300"
                          />
                        )}
                        <button
                          data-testid={`admin-field-${key}-save`}
                          onClick={() => saveField(key)}
                          disabled={saving[key]}
                          className="mt-4 inline-flex items-center gap-2 text-xs bg-[var(--accent)] hover:bg-[var(--accent-soft)] disabled:opacity-60 text-white font-semibold px-4 py-2.5 rounded-full transition-colors duration-300"
                        >
                          {saving[key] ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                          {saving[key] ? t("admin_saving") : t("admin_save")}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}

            <section id={`content-section-${GROUPS.length}`} className="scroll-mt-44">
              <h2 className="font-display font-semibold text-2xl mb-3 flex items-center gap-3">
                <span className="w-6 h-px bg-[var(--accent)]" />
                {t("admin_media")}
              </h2>
              <p className="text-xs text-white/40 mb-6 max-w-2xl">{t("admin_media_hint")}</p>
              <div className="grid md:grid-cols-2 gap-4">
                {MEDIA_FIELDS.map((mf) => {
                  const val = fields[mf.key]?.fr ?? "";
                  return (
                    <MediaField
                      key={mf.key}
                      label={mf.label}
                      fieldKey={mf.key}
                      value={val}
                      saving={!!saving[mf.key]}
                      uploading={!!uploading[mf.key]}
                      onText={(v) => setMediaText(mf.key, v)}
                      onFile={(file) => uploadMedia(mf.key, file)}
                      onSave={() => saveField(mf.key)}
                      t={t}
                      hint={mf.hintKey ? t(mf.hintKey) : null}
                    />
                  );
                })}
              </div>
            </section>
          </div>
        )}

        {tab === "gallery" && <GalleryManager />}

        {tab === "stats" && <StatsPanel />}

        {tab === "testimonials" && <TestimonialsManager />}

        {tab === "design" && <DesignPanel />}

        {tab === "news" && <NewsManager />}
      </main>
    </div>
  );
}
